import moment from 'moment';

import * as Types from '../model/types';
import * as OpKinds from '../opKinds';
import * as Graph from '../graph';
import * as Urls from '../_external/util/urls';

const makeArtifactOp = OpKinds.makeStandardOp;

const artifactArgDescription = `A ${Graph.docType('artifact')}`;

const artifactArgTypes = {
  artifact: 'artifact' as const,
};

export const opArtifactName = makeArtifactOp({
  name: 'artifact-name',
  argTypes: artifactArgTypes,
  description: `Returns the name of the ${Graph.docType('artifact')}`,
  argDescriptions: {artifact: artifactArgDescription},
  returnValueDescription: `The name of the ${Graph.docType('artifact')}`,
  returnType: inputTypes => 'string',
  resolver: ({artifact}) => artifact.name,
});

export const opArtifactAliases = makeArtifactOp({
  hidden: true,
  name: 'artifact-aliases',
  argTypes: artifactArgTypes,
  returnType: inputTypes => Types.list('artifactAlias'),
  resolver: ({artifact}) =>
    artifact.aliases.edges.map((edge: any) => edge.node),
});

// Hiding, We don't like the word "type" here.
export const opArtifactType = makeArtifactOp({
  hidden: true,
  name: 'artifact-type',
  argTypes: artifactArgTypes,
  description: `Returns the type of the ${Graph.docType('artifact')}`,
  argDescriptions: {artifact: artifactArgDescription},
  returnValueDescription: `The type of the ${Graph.docType('artifact')}`,
  returnType: inputTypes => Types.maybe('artifactType'),
  resolver: ({artifact}) => artifact.defaultArtifactType,
});

export const opArtifactVersions = makeArtifactOp({
  name: 'artifact-versions',
  argTypes: artifactArgTypes,
  description: `Returns the versions of the ${Graph.docType('artifact')}`,
  argDescriptions: {artifact: artifactArgDescription},
  returnValueDescription: `The versions of the ${Graph.docType('artifact')}`,
  returnType: inputTypes => Types.list('artifactVersion'),
  resolver: ({artifact}) =>
    artifact.artifacts.edges.map((version: any) => version.node),
});

export const opArtifactCreatedAt = makeArtifactOp({
  hidden: true,
  name: 'artifact-createdAt',
  argTypes: artifactArgTypes,
  description: `Returns the creation date of the ${Graph.docType('artifact')}`,
  argDescriptions: {artifact: artifactArgDescription},
  returnValueDescription: `The creation date of the ${Graph.docType(
    'artifact'
  )}`,
  returnType: inputTypes => 'date',
  resolver: ({artifact}) => moment(artifact.createdAt + 'Z').toDate(),
});

export const opArtifactProject = makeArtifactOp({
  hidden: true,
  name: 'artifact-project',
  argTypes: artifactArgTypes,
  description: `Returns the ${Graph.docType('project')} of the ${Graph.docType(
    'artifact'
  )}`,
  argDescriptions: {artifact: artifactArgDescription},
  returnValueDescription: `The ${Graph.docType(
    'project'
  )} of the ${Graph.docType('artifact')}`,
  returnType: inputTypes => 'project',
  resolver: ({artifact}) => artifact.project,
});

export const opArtifactLink = makeArtifactOp({
  name: 'artifact-link',
  argTypes: artifactArgTypes,
  description: `Returns the url for a ${Graph.docType('artifact')}`,
  argDescriptions: {artifact: artifactArgDescription},
  returnValueDescription: `The url for a ${Graph.docType('artifact')}`,
  returnType: inputTypes => 'link',
  resolver: ({artifact}) => {
    return {
      name: artifact.name,
      url: Urls.artifactCollection({
        entityName: artifact.project.entity.name,
        projectName: artifact.project.name,
        artifactTypeName: artifact.defaultArtifactType.name,
        artifactCollectionName: artifact.name,
      }),
    };
  },
});

export const opArtifactMemberships = makeArtifactOp({
  hidden: true,
  name: 'artifact-memberships',
  argTypes: artifactArgTypes,
  returnType: inputTypes => Types.list('artifactMembership'),
  resolver: ({artifact}) =>
    (artifact.artifactMemberships.edges ?? []).map((e: any) => e.node),
});

export const opArtifactMembershipForAlias = makeArtifactOp({
  hidden: true,
  name: 'artifact-membershipForAlias',
  argTypes: {
    artifact: 'artifact' as const,
    aliasName: 'string' as const,
  },
  returnType: inputTypes => {
    return 'artifactMembership';
  },
  resolver: ({artifact, aliasName}) =>
    artifact[`artifactMembership_${aliasName}`],
});
